import { useEffect, useState } from 'react';
import styles from '../AdminPortal.module.scss';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { FlexIcon } from '../../../components/primitives/FlexIcon';
import AdminOrganizations from '../elements/AdminOrganizations';
import { useM2MApiContext } from '../../../api/m2mApi';
import { TpOrganizationInfo, TpPluginInfo } from '../../../api/models/admin';
import { useUser } from '../../../app/hooks';
import { useNavigate } from 'react-router-dom';
import { TpUserInfo } from '../../../data/interfaces';
import AdminUsers from '../elements/AdminUsers';

type TpAdminPortalElement = {
  id: string;
  title: string;
  icon: string;
  component: any;
};

const AdminPortal = () => {
  const [activeTab, setActiveTab] = useState<TpAdminPortalElement>({} as TpAdminPortalElement);
  const systemAdminApi = useM2MApiContext().systemAdmin;
  const { user } = useUser();
  const navigate = useNavigate();

  const [AdminPortalHeaders, setAdminPortalHeaders] = useState<TpAdminPortalElement[]>([]);
  const [organizations, setOrganizations] = useState<TpOrganizationInfo[]>([]);
  const [plugins, setPlugins] = useState<TpPluginInfo[]>([]);
  const [users, setUsers] = useState<TpUserInfo[]>([]);

  const updateOrganizations = () => {
    systemAdminApi.listOrganizations().then((orgs) => {
      const orderedOrgs = orgs.sort((a, b) => a.name.localeCompare(b.name));
      setOrganizations(orderedOrgs);
    });
  };

  const updateUsers = () => {
    systemAdminApi.listUsers().then((users) => {
      const orderedUsers = users.sort((a, b) => a.email!.localeCompare(b.email!));
      setUsers(orderedUsers);
    });
  };

  useEffect(() => {
    if (!user.isSystemAdmin) {
      navigate('/');
    }
    setAdminPortalHeaders([]);
    const validTabs: TpAdminPortalElement[] = [];

    systemAdminApi
      .listOrganizations()
      .then((orgs) => {
        if (orgs.length > 0) {
          const orderedOrgs = orgs.sort((a, b) => a.name.localeCompare(b.name));
          setOrganizations(orderedOrgs);
          const organizationTab = {
            id: 'organizations',
            title: 'Organizations',
            icon: 'fad:sitemap',
            component: AdminOrganizations,
          };
          validTabs.push(organizationTab);
          setActiveTab(organizationTab);
        }
        return systemAdminApi.listUsers();
      })
      .then((users) => {
        const orderedUsers = users.sort((a, b) => a.email!.localeCompare(b.email!));
        setUsers(orderedUsers);
        const userTab = {
          id: 'users',
          title: 'Users',
          icon: 'fad:users',
          component: AdminUsers,
        };
        validTabs.push(userTab);
        return systemAdminApi.listPlugins();
      })
      .then((plugins) => {
        if (plugins.length > 0) {
          setPlugins(plugins);
        }
      });
    setAdminPortalHeaders(validTabs);
  }, []);

  return (
    <>
      <div className={styles.adminHeaderWrapper}>
        {AdminPortalHeaders.map((element: TpAdminPortalElement, index: number) => {
          return (
            <div key={`${element.id}-${index}`} style={{ display: 'inline-block', margin: '10px 30px' }}>
              <FancyButton
                iconName={element.icon}
                large={true}
                onClick={() => {
                  setActiveTab(element);
                }}
              >
                {element.title}
              </FancyButton>
            </div>
          );
        })}
      </div>
      {Object.keys(activeTab).length > 0 && (
        <>
          <h1 className={styles.adminContentTitle}>
            <FlexIcon iconName={activeTab.icon} /> {activeTab.title}
          </h1>
          <div className={styles.adminContentWrapper}>
            <activeTab.component
              organizations={organizations}
              plugins={plugins}
              users={users}
              updateOrganizations={updateOrganizations}
              updateUsers={updateUsers}
            />
          </div>
        </>
      )}
    </>
  );
};

export default AdminPortal;
